import { useEffect, useState } from 'react';

import { Loading3QuartersOutlined } from '@ant-design/icons';
import { Button, Colors, Flex, Text, CloseLine } from '@revenuemarket/cds';
import { ModalProps, Tooltip } from 'antd';
import Image from 'next/image';

import { CompanyUserPermission } from '~apis/role/type';
import { PermissionGuard } from '~components/Role/PermissionGuard';
import useCertificate from '~hooks/useCertificate';

import CertificateSection from './CertificateSection';
import CodefDownloadSection from './CodefDownloadSection';
import RegisteredCertList from './RegisteredCertList';
import CommonModal from '../CommonModal';
import PermissionInfoModal from '../PermissionInfoModal';

interface Props extends ModalProps {}

const CertificateRegisterModal = ({ ...props }: Props) => {
  const [codefNotInstalled, setCodefNotInstalled] = useState(false);
  const { isVerifying, checkCodefInstalled } = useCertificate();
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const check = async () => {
      const { downloadNeeded } = await checkCodefInstalled();

      if (downloadNeeded) {
        setCodefNotInstalled(true);
      }
    };

    check();
  }, []);

  return (
    <PermissionGuard
      required={CompanyUserPermission.CAN_MANAGE_DATA_INTEGRATION}
      fallback={
        <PermissionInfoModal
          {...props}
          type="function"
          requiredPermission={[CompanyUserPermission.CAN_MANAGE_DATA_INTEGRATION]}
        />
      }
    >
      <CommonModal
        {...props}
        footer={null}
        width={'max-content'}
        centered
        style={{
          minWidth: 540,
        }}
        closeElement={
          <Tooltip title={isLoading ? '연동하는 중에는 이 창을 닫을 수 없어요' : ''}>
            <Button
              onClick={(e) => {
                if (isLoading) {
                  return;
                }
                props.onCancel?.(e);
              }}
              buttonType="transparent"
              buttonSize="large"
              icon={
                <CloseLine
                  width={20}
                  height={20}
                  fill={isLoading ? Colors.grayOpacity22 : Colors.gray700}
                />
              }
            />
          </Tooltip>
        }
        title={
          <Text
            textStyle="24_600"
            style={{
              color: Colors.gray900,
            }}
          >
            공동인증서 연동하기
          </Text>
        }
      >
        <Flex gap={isVerifying ? 0 : 24} style={{ width: '100%' }}>
          <div style={{ paddingTop: 24, width: 500 }}>
            {isVerifying ? (
              <Flex
                direction="column"
                gap={16}
                align="center"
                style={{
                  paddingTop: 24,
                  paddingBottom: 16,
                }}
              >
                <Image
                  src="https://static.clobe.ai/image/cert-program-find.png"
                  alt="codef-monitor-img"
                  width={100}
                  height={100}
                  objectFit="contain"
                />
                <Flex gap={8} align="center">
                  <Loading3QuartersOutlined
                    spin
                    style={{
                      fontSize: 18,
                      color: Colors.gray500,
                    }}
                  />
                  <Text textStyle="18_600" style={{ color: Colors.gray900 }}>
                    공동인증서 연동을 위한 프로그램 설치여부 확인중...
                  </Text>
                </Flex>
              </Flex>
            ) : codefNotInstalled ? (
              <CodefDownloadSection setDownloadNeeded={setCodefNotInstalled} />
            ) : (
              <CertificateSection isLoading={isLoading} setIsLoading={setIsLoading} />
            )}
          </div>
          {!isVerifying && <RegisteredCertList />}
        </Flex>
      </CommonModal>
    </PermissionGuard>
  );
};

export default CertificateRegisterModal;
